import { type ChangeEvent, useId, useState } from "react";
import { cn } from "../lib/utils";
import {
  type AuteurJeu,
  type ImportYamlJeu,
  type LivreJeuDetail,
  YAML_LIVRE_MAX,
} from "./contract";
import {
  ALERTE_GRISE,
  ALERTE_JAUNE,
  ALERTE_PANNE,
  ALERTE_VERTE,
  BOUTON_CONTOUR,
  Carte,
  CHAMP_YAML,
  LIEN_BOUTON_PLEIN,
  Rapport,
  telecharger,
} from "./livres-jeux-commun";
import {
  messageDEchec,
  RefusJeu,
  useExportYaml,
  useImportYaml,
  useMiseEnPage,
  useVerification,
} from "./livres-jeux-requetes";
import { compterJeu, nomDeFichier, octetsDuBase64 } from "./livres-jeux-textes";

/**
 * L'onglet Livre (spec du 03/10/2026, D2, D5 et D6) : verifier le livre entier, le mettre en
 * page (le PDF se telecharge, rien n'est garde), l'exporter en YAML pour le verser au depot, et
 * importer un livre entier en YAML : l'apercu d'abord, puis le remplacement.
 */

export function OngletLivre({ detail }: { detail: LivreJeuDetail }) {
  return (
    <div className="grid items-start gap-5 lg:grid-cols-2">
      <Verifier livreId={detail.livre.id} vide={detail.scenes.length === 0} />
      <MettreEnPage
        cle={detail.livre.cle}
        livreId={detail.livre.id}
        vide={detail.scenes.length === 0}
      />
      <Importer detail={detail} />
      <Exporter cle={detail.livre.cle} livreId={detail.livre.id} />
    </div>
  );
}

function Verifier({ livreId, vide }: { livreId: number; vide: boolean }) {
  const verification = useVerification(livreId);
  return (
    <Carte id="livres-jeux-verifier" titre="Vérifier">
      <p className="text-sm text-muted-foreground">
        Le moteur explore toutes les parties : scènes inatteignables, cibles inconnues, impasses,
        énigmes sans issue. Chaque faute mène à sa scène.
      </p>
      <div>
        <button
          className={LIEN_BOUTON_PLEIN}
          disabled={verification.isFetching || vide}
          onClick={() => void verification.refetch()}
          type="button"
        >
          {verification.isFetching ? "Vérification..." : "Vérifier le livre"}
        </button>
      </div>
      {verification.isError ? (
        <p className={ALERTE_PANNE} role="alert">
          {messageDEchec(verification.error)}
        </p>
      ) : null}
      {verification.data ? <Rapport rapport={verification.data} /> : null}
    </Carte>
  );
}

function MettreEnPage({ livreId, cle, vide }: { livreId: number; cle: string; vide: boolean }) {
  const miseEnPage = useMiseEnPage(livreId);
  const refus = miseEnPage.error instanceof RefusJeu ? miseEnPage.error : null;
  return (
    <Carte id="livres-jeux-pdf" titre="Mettre en page">
      <p className="text-sm text-muted-foreground">
        Le PDF du livre, prêt à imprimer : rien n'est gardé, chaque clic refait la mise en page.
      </p>
      <div>
        <button
          className={LIEN_BOUTON_PLEIN}
          disabled={miseEnPage.isPending || vide}
          onClick={() =>
            miseEnPage.mutate(undefined, {
              onSuccess: (rendu) =>
                telecharger(octetsDuBase64(rendu.pdf), "application/pdf", nomDeFichier(cle, "pdf")),
            })
          }
          type="button"
        >
          {miseEnPage.isPending ? "Mise en page..." : "Mettre en page et télécharger"}
        </button>
      </div>
      {miseEnPage.data ? (
        <div className="flex flex-col gap-2 text-sm" role="status">
          <p className={ALERTE_VERTE}>
            PDF de {compterJeu(miseEnPage.data.pages, "page", "pages")} téléchargé (
            {nomDeFichier(cle, "pdf")}).
          </p>
          {miseEnPage.data.decalages.length === 0 ? null : (
            <div className={ALERTE_JAUNE}>
              <p className="font-semibold">Ce qui déborde de sa page :</p>
              <ul className="list-disc pl-5">
                {miseEnPage.data.decalages.map((decalage) => (
                  <li key={decalage}>{decalage}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}
      {refus?.rapport ? (
        <Rapport rapport={refus.rapport} />
      ) : miseEnPage.isError ? (
        <p className={ALERTE_PANNE} role="alert">
          {messageDEchec(miseEnPage.error)}
        </p>
      ) : null}
    </Carte>
  );
}

function Exporter({ livreId, cle }: { livreId: number; cle: string }) {
  const exporter = useExportYaml(livreId);
  return (
    <Carte id="livres-jeux-exporter" titre="Exporter en YAML">
      <p className="text-sm text-muted-foreground">
        Le livre entier au format du moteur : de quoi le verser au dépôt, à côté de la démo.
      </p>
      <div>
        <button
          className={BOUTON_CONTOUR}
          disabled={exporter.isPending}
          onClick={() =>
            exporter.mutate(undefined, {
              onSuccess: (yaml) => telecharger(yaml, "application/yaml", nomDeFichier(cle, "yaml")),
            })
          }
          type="button"
        >
          {exporter.isPending ? "Export..." : `Télécharger ${nomDeFichier(cle, "yaml")}`}
        </button>
      </div>
      {exporter.isError ? (
        <p className={ALERTE_PANNE} role="alert">
          {messageDEchec(exporter.error)}
        </p>
      ) : null}
    </Carte>
  );
}

/**
 * Importer un livre entier : coller le YAML ou choisir le fichier, dire qui en a ecrit les
 * textes (le plus souvent le premier jet de Claude), voir ce qui serait lu et ce qui serait
 * remplace, puis remplacer. La trame reste ; le livre garde son identifiant ; le journal garde le
 * texte de chaque scene remplacee.
 */
function Importer({ detail }: { detail: LivreJeuDetail }) {
  const importer = useImportYaml(detail.livre.id);
  const [yaml, setYaml] = useState("");
  const [auteur, setAuteur] = useState<AuteurJeu>("claude");
  const idAuteur = useId();
  const [apercu, setApercu] = useState<ImportYamlJeu["apercu"] | null>(null);
  const [fait, setFait] = useState<number | null>(null);
  const idZone = useId();
  const refus = importer.error instanceof RefusJeu ? importer.error : null;

  const changer = (texte: string): void => {
    setYaml(texte);
    setApercu(null);
    setFait(null);
    importer.reset();
  };

  const lireFichier = async (evenement: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const fichier = evenement.target.files?.[0];
    if (fichier === undefined) return;
    changer(await fichier.text());
  };

  return (
    <Carte className="lg:col-span-2" id="livres-jeux-importer" titre="Importer un livre entier">
      <p className="text-sm text-muted-foreground">
        Un YAML au format du moteur (le premier jet de Claude, un livre du dépôt) remplace les
        réglages et toutes les scènes ; la trame reste. Tu vois d'abord ce qui serait importé.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <label
          className={cn(
            BOUTON_CONTOUR,
            "cursor-pointer focus-within:outline-none focus-within:ring-2 focus-within:ring-ring",
          )}
        >
          Choisir un fichier
          <input
            accept=".yaml,.yml,.json,text/yaml,application/yaml"
            className="sr-only"
            onChange={(evenement) => void lireFichier(evenement)}
            type="file"
          />
        </label>
        <span className="text-xs text-muted-foreground">ou colle le YAML ci-dessous</span>
      </div>
      <label className="sr-only" htmlFor={idZone}>
        Le livre en YAML
      </label>
      <textarea
        className={CHAMP_YAML}
        id={idZone}
        maxLength={YAML_LIVRE_MAX}
        onChange={(evenement) => changer(evenement.target.value)}
        placeholder={
          "id: manoir\ntitre: Le manoir\ndepart: entree\nscenes:\n  - id: entree\n    texte: ..."
        }
        spellCheck={false}
        value={yaml}
      />
      <fieldset className="flex flex-col gap-1.5 text-sm">
        <legend className="mb-1 font-medium">Qui a écrit ces textes ?</legend>
        <label className="flex min-h-8 items-center gap-2">
          <input
            checked={auteur === "claude"}
            name={idAuteur}
            onChange={() => setAuteur("claude")}
            type="radio"
          />
          Claude : son premier jet, ou un livre du dépôt
        </label>
        <label className="flex min-h-8 items-center gap-2">
          <input
            checked={auteur === "nicolas"}
            name={idAuteur}
            onChange={() => setAuteur("nicolas")}
            type="radio"
          />
          Moi
        </label>
      </fieldset>
      <div className="flex flex-wrap gap-2">
        <button
          className={BOUTON_CONTOUR}
          disabled={yaml.trim() === "" || importer.isPending}
          onClick={() =>
            importer.mutate(
              { yaml, essai: true, auteur },
              { onSuccess: (reponse) => setApercu(reponse.apercu) },
            )
          }
          type="button"
        >
          Voir ce qui serait importé
        </button>
        {apercu === null ? null : (
          <button
            className={LIEN_BOUTON_PLEIN}
            disabled={importer.isPending}
            onClick={() =>
              importer.mutate(
                { yaml, essai: false, auteur },
                {
                  onSuccess: (reponse) => {
                    setApercu(null);
                    setFait(reponse.apercu.scenes);
                  },
                },
              )
            }
            type="button"
          >
            Remplacer le livre
          </button>
        )}
      </div>
      {apercu === null ? null : (
        <div className={apercu.remplace.ecrites > 0 ? ALERTE_JAUNE : ALERTE_GRISE} role="status">
          <p>
            <strong className="font-semibold">{apercu.titre}</strong> :{" "}
            {compterJeu(apercu.scenes, "scène", "scènes")} à importer
            {apercu.cle_yaml === detail.livre.cle
              ? "."
              : ` (le YAML s'appelle « ${apercu.cle_yaml} », le livre garde « ${detail.livre.cle} »).`}
          </p>
          <p>
            {apercu.remplace.scenes === 0
              ? "Le livre n'a pas encore de scène : rien n'est remplacé."
              : `Elles remplacent les ${compterJeu(apercu.remplace.scenes, "scène", "scènes")} du livre, dont ${compterJeu(apercu.remplace.ecrites, "a un texte", "ont un texte", "aucune n'a de texte")}.`}
            {apercu.remplace.ecrites > 0
              ? " Leur texte quitte le livre mais reste au journal : une scène du même identifiant le retrouve dans « Textes d'avant ». Pour tout garder sous la main, exporte d'abord le livre (ci-dessus)."
              : null}
          </p>
        </div>
      )}
      {fait === null ? null : (
        <p className={ALERTE_VERTE} role="status">
          Livre importé : {compterJeu(fait, "scène", "scènes")}. Vérifie-le avant de le mettre en
          page.
        </p>
      )}
      {refus !== null && refus.erreurs.length > 0 ? (
        <div className={ALERTE_PANNE} role="alert">
          <p className="font-semibold">{refus.message}</p>
          <ul className="mt-1 list-disc pl-5 [overflow-wrap:anywhere]">
            {refus.erreurs.slice(0, 30).map((erreur) => (
              <li key={erreur}>{erreur}</li>
            ))}
          </ul>
          {refus.erreurs.length > 30 ? (
            <p className="mt-1">et {refus.erreurs.length - 30} autres.</p>
          ) : null}
        </div>
      ) : importer.isError ? (
        <p className={ALERTE_PANNE} role="alert">
          {messageDEchec(importer.error)}
        </p>
      ) : null}
    </Carte>
  );
}
